import React, { useState, useMemo } from 'react';
import { useTranslation } from 'utils/i18n';
import sharedTranslations from '../shared/translations';
import { colors, typography, spacing, interactive } from './docsStyles';

const indent = (level) => ({ paddingLeft: `${(level - 1) * 12}px` });

const TocItem = ({ node, level = 1, activeId, language }) => {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isActive = activeId === node.id;
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      collapseAriaLabel: t?.legal?.toc?.collapseAriaLabel || 'Collapse section',
      expandAriaLabel: t?.legal?.toc?.expandAriaLabel || 'Expand section'
    };
  }, [language]);

  const itemStyle = {
    marginBottom: spacing.xs
  };

  const linkContainerStyle = {
    ...indent(level),
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    transition: 'background-color 0.2s ease'
  };

  const linkStyle = {
    ...interactive.link,
    textDecoration: 'none',
    color: isActive ? colors.primary : colors.text,
    fontWeight: isActive ? '600' : '400',
    flex: 1
  };

  const iconStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: colors.textSecondary,
    padding: 0,
    width: '16px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div className={`docs-toc-item level-${level}`} style={itemStyle}>
      <div
        className={`docs-toc-link ${isActive ? 'active' : ''}`}
        style={linkContainerStyle}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.backgroundColor = colors.backgroundHover;
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        {hasChildren ? (
          <button
            aria-label={open ? translations.collapseAriaLabel : translations.expandAriaLabel}
            onClick={() => setOpen(!open)}
            style={iconStyle}
            onMouseEnter={(e) => {
              e.target.style.color = colors.text;
            }}
            onMouseLeave={(e) => {
              e.target.style.color = colors.textSecondary;
            }}
          >
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span style={{ width: '16px' }} />
        )}
        <a 
          href={`#${node.id}`} 
          style={linkStyle}
          onClick={(e) => {
            e.preventDefault();
            const element = document.getElementById(node.id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              // Update URL without scrolling
              window.history.pushState(null, '', `#${node.id}`);
            }
          }}
        >
          {node.title}
        </a>
      </div>
      {hasChildren && open ? (
        <div>
          {node.children.map((child) => (
            <TocItem key={child.id} node={child} level={level + 1} activeId={activeId} language={language} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const flatten = (sections) => sections.map(s => ({
  id: s.id,
  title: s.title,
  children: s.children ? flatten(s.children) : []
}));

const DocsToc = ({ sections = [], activeId }) => {
  const { language } = useTranslation();
  const tree = useMemo(() => flatten(sections), [sections]);
  
  // Get translations - memoized to update when language changes
  const translations = useMemo(() => {
    const t = sharedTranslations[language] || sharedTranslations.en;
    return {
      ariaLabel: t?.legal?.toc?.ariaLabel || 'Table of contents'
    };
  }, [language]);
  
  const navStyle = {
    marginTop: spacing.md
  };

  return (
    <nav className="docs-toc" aria-label={translations.ariaLabel} style={navStyle}>
      {tree.map(node => (
        <TocItem key={node.id} node={node} level={1} activeId={activeId} language={language} />
      ))}
    </nav>
  );
};

export default DocsToc;




